import { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { StatusBadge, RatingBadge } from '../components/VisualAtoms';
import ExhibitionPublicUrlStrip from '../components/ExhibitionPublicUrlStrip';
import { humanize, toneForOpenCallStatus } from '../theme/adminVisualSystem';
import SectionAnalyticsHeader from '../components/SectionAnalyticsHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['all', 'new', 'under_review', 'shortlisted', 'accepted', 'rejected', 'waitlist', 'contacted'];

export default function OpenCallListPage() {
  const { exhibitionId, selectedExhibition } = useOutletContext();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!exhibitionId) return;
    setLoading(true);
    // Always fetch ALL — frontend filters so analytics totals stay consistent
    axios
      .get(`${API}/admin/open-call?exhibition_id=${exhibitionId}`)
      .then((r) => setApps(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [exhibitionId]);

  const filtered = useMemo(() => {
    let arr = apps;
    if (filter !== 'all') arr = arr.filter((a) => (a.status || 'new') === filter);
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(
        (a) =>
          (a.artist_name || '').toLowerCase().includes(s) ||
          (a.email || '').toLowerCase().includes(s) ||
          (a.location || '').toLowerCase().includes(s),
      );
    }
    return arr;
  }, [apps, filter, search]);

  // Totals for analytics
  const totals = useMemo(() => {
    const t = { new: 0, under_review: 0, shortlisted: 0, accepted: 0, rejected: 0, waitlist: 0, contacted: 0 };
    for (const a of apps) {
      const s = a.status || 'new';
      if (t[s] !== undefined) t[s] += 1;
    }
    return t;
  }, [apps]);

  return (
    <div className="space-y-6">
      {selectedExhibition?.slug && (
        <ExhibitionPublicUrlStrip
          slug={selectedExhibition.slug}
          hash="opencall"
          label="Public open-call form"
          disabled={!selectedExhibition.open_call_enabled}
          disabledReason="Open call is closed for this exhibition. Enable it on the Exhibitions tab to share a public form."
        />
      )}
      <SectionAnalyticsHeader
        title="Open Call Applications"
        helper="Submission inflow across the curatorial review pipeline"
        items={apps}
        chartLabel="Submission Inflow"
        days={14}
        activeStatKey={filter}
        onStatClick={setFilter}
        stats={[
          { key: 'new', label: 'New', value: totals.new, tone: toneForOpenCallStatus('new') },
          { key: 'under_review', label: 'Review', value: totals.under_review, tone: toneForOpenCallStatus('under_review') },
          { key: 'shortlisted', label: 'Shortlist', value: totals.shortlisted, tone: toneForOpenCallStatus('shortlisted') },
          { key: 'accepted', label: 'Accepted', value: totals.accepted, tone: toneForOpenCallStatus('accepted') },
          { key: 'waitlist', label: 'Waitlist', value: totals.waitlist, tone: toneForOpenCallStatus('waitlist') },
          { key: 'rejected', label: 'Rejected', value: totals.rejected, tone: toneForOpenCallStatus('rejected') },
        ]}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-clickable="true"
            className={`text-[10px] font-mono px-3 py-1.5 border rounded-none uppercase tracking-[0.14em] transition-all ${
              filter === s
                ? 'border-[#90E0EF] text-[#90E0EF] bg-[#90E0EF]/5 shadow-[0_0_10px_rgba(144,224,239,0.18)]'
                : 'border-[#222] text-[#777] hover:text-white hover:border-[#333]'
            }`}
          >
            {s === 'all' ? 'All' : humanize(s)}
          </button>
        ))}
      </div>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search artist, email, location..."
        className="bg-transparent border border-[#222] text-white text-xs font-mono px-3 py-2 w-full sm:w-72 focus:outline-none focus:border-[#90E0EF]"
      />

      {/* Table */}
      <div className="border border-[#1a1a1a] overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="border-b border-[#1a1a1a] text-[#666] uppercase tracking-[0.15em]">
              <th className="text-left p-3">Artist</th>
              <th className="text-left p-3">Medium</th>
              <th className="text-left p-3">Location</th>
              <th className="text-left p-3">Attendance</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Rating</th>
              <th className="text-left p-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-[#444]">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-[#444]">No applications found</td></tr>
            ) : filtered.map((a, i) => (
              <motion.tr
                key={a.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.02 }}
                onClick={() => navigate(`/admin/open-call/${a.id}`)}
                data-clickable="true"
                className="border-b border-[#111] hover:bg-[#111] hover:border-[#90E0EF]/15 transition-colors cursor-pointer"
              >
                <td className="p-3 text-[#f6f6f6]">{a.artist_name}</td>
                <td className="p-3 text-[#aaa]">{humanize(a.main_medium_style) || '—'}</td>
                <td className="p-3 text-[#aaa]">{a.location}</td>
                <td className="p-3 text-[#888]">{a.venue_attendance}</td>
                <td className="p-3"><StatusBadge status={a.status} kind="open_call" /></td>
                <td className="p-3"><RatingBadge rating={a.curator_rating} /></td>
                <td className="p-3 text-[#666]">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
